import React from 'react';
import { observer } from 'mobx-react-lite';
import {
  Box,
  Typography,
  IconButton,
  Switch,
  Slider,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { t } from '../config';

const FILTER_PRESETS = [
  { name: 'Grayscale', key: 'grayscale', bg: '#e0e0e0', color: '#888' },
  { name: 'Sepia', key: 'sepia', bg: '#f5e6c8', color: '#a67c52' },
  { name: 'Cold', key: 'cold', bg: '#d0e4f7', color: '#6a9fd8' },
  { name: 'Natural', key: 'natural', bg: '#f0f0f0', color: '#777' },
  { name: 'Warm', key: 'warm', bg: '#fbe8d3', color: '#c08050' },
];

const EffectRow = ({ label, enabled, onToggle, value, onChange, min, max, step, children }) => (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>{label}</Typography>
      <Switch size="small" checked={enabled} onChange={(e) => onToggle(e.target.checked)} />
    </Box>
    {enabled && (
      <>
        {children}
        {value !== undefined && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Slider
              size="small"
              value={value}
              onChange={(_, v) => onChange(v)}
              min={min || 0}
              max={max || 100}
              step={step || 1}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              type="number"
              value={typeof value === 'number' ? Math.round(value * 10) / 10 : value}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                if (!isNaN(v)) onChange(v);
              }}
              onFocus={(e) => e.target.select()}
              sx={{ width: 65, '& .MuiInputBase-input': { fontSize: 12, py: 0.5, textAlign: 'center' } }}
              inputProps={{ min, max, step }}
            />
          </Box>
        )}
      </>
    )}
  </Box>
);

const ImageEffectsPanel = observer(({ store }) => {
  const panel = store._toolbarPanel;
  if (!panel || panel.type !== 'effects') return null;

  const element = store.getElementById(panel.elementId);
  if (!element) return null;

  const handleClose = () => store.closeToolbarPanel();

  const commit = () => store.history.addUndoState();

  const resetAll = { grayscaleEnabled: false, sepiaEnabled: false, warmEnabled: false, coldEnabled: false, brightnessEnabled: false, brightness: 0, blurEnabled: false };

  const applyPreset = (key) => {
    const presets = {
      grayscale: { ...resetAll, grayscaleEnabled: true },
      sepia:     { ...resetAll, sepiaEnabled: true },
      cold:      { ...resetAll, coldEnabled: true },
      natural:   { ...resetAll },
      warm:      { ...resetAll, warmEnabled: true },
    };
    element.set(presets[key] || resetAll);
    commit();
  };

  const getActivePreset = () => {
    if (element.grayscaleEnabled) return 'grayscale';
    if (element.sepiaEnabled) return 'sepia';
    if (element.warmEnabled) return 'warm';
    if (element.coldEnabled) return 'cold';
    return null;
  };

  return (
    <Box
      sx={{
        width: 280,
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        backgroundColor: '#fff',
        borderRight: '1px solid #e0e0e0',
        flexShrink: 0,
        p: 2,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t('toolbar.effects', 'Effects')}</Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Filter presets */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 3 }}>
        {FILTER_PRESETS.map((preset) => {
          const isActive = getActivePreset() === preset.key;
          return (
            <Box
              key={preset.key}
              onClick={() => applyPreset(preset.key)}
              sx={{
                cursor: 'pointer',
                textAlign: 'center',
                borderRadius: '8px',
                border: isActive ? '2px solid #0d83cd' : '2px solid transparent',
                p: 0.5,
                '&:hover': { backgroundColor: '#f5f5f5' },
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: '1',
                  borderRadius: '6px',
                  backgroundColor: preset.bg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 0.5,
                  overflow: 'hidden',
                }}
              >
                {element.src ? (
                  <img
                    src={element.src}
                    alt={preset.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      filter:
                        preset.key === 'grayscale' ? 'grayscale(1)' :
                        preset.key === 'sepia' ? 'sepia(1)' :
                        preset.key === 'cold' ? 'saturate(0.5) hue-rotate(180deg)' :
                        preset.key === 'warm' ? 'saturate(1.3) sepia(0.2)' :
                        'none',
                    }}
                  />
                ) : (
                  <Typography variant="caption" sx={{ color: preset.color, fontSize: 10 }}>
                    {preset.name[0]}
                  </Typography>
                )}
              </Box>
              <Typography variant="caption" sx={{ fontSize: 10 }}>{preset.name}</Typography>
            </Box>
          );
        })}
      </Box>

      {/* Blur */}
      <EffectRow
        label={t('toolbar.blur', 'Blur')}
        enabled={element.blurEnabled}
        onToggle={(v) => { element.set({ blurEnabled: v }); commit(); }}
        value={element.blurRadius}
        onChange={(v) => element.set({ blurRadius: v })}
        min={0}
        max={40}
        step={1}
      />

      {/* Brightness */}
      <EffectRow
        label={t('toolbar.brightness', 'Brightness')}
        enabled={element.brightnessEnabled}
        onToggle={(v) => { element.set({ brightnessEnabled: v }); commit(); }}
        value={Math.round(element.brightness * 100)}
        onChange={(v) => element.set({ brightness: v / 100 })}
        min={-100}
        max={100}
        step={1}
      />

      {/* Border */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{t('toolbar.border', 'Border')}</Typography>
          <Switch
            size="small"
            checked={element.borderSize > 0}
            onChange={(e) => {
              element.set({ borderSize: e.target.checked ? 2 : 0 });
              commit();
            }}
          />
        </Box>
        {element.borderSize > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <input
              type="color"
              value={element.borderColor || '#000000'}
              onChange={(e) => { element.set({ borderColor: e.target.value }); commit(); }}
              style={{ width: 32, height: 32, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', padding: 0 }}
            />
            <TextField
              size="small"
              type="number"
              value={element.borderSize}
              onChange={(e) => {
                const v = parseInt(e.target.value);
                if (!isNaN(v) && v >= 0) { element.set({ borderSize: v }); commit(); }
              }}
              onFocus={(e) => e.target.select()}
              sx={{ width: 65, '& .MuiInputBase-input': { fontSize: 12, py: 0.5, textAlign: 'center' } }}
              inputProps={{ min: 0 }}
            />
          </Box>
        )}
      </Box>

      {/* Corner Radius */}
      <EffectRow
        label={t('toolbar.cornerRadius', 'Corner Radius')}
        enabled={element.cornerRadius > 0}
        onToggle={(v) => { element.set({ cornerRadius: v ? 10 : 0 }); commit(); }}
        value={element.cornerRadius}
        onChange={(v) => element.set({ cornerRadius: v })}
        min={0}
        max={Math.min(element.width, element.height) / 2}
        step={1}
      />

      {/* Shadow */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{t('toolbar.shadow', 'Shadow')}</Typography>
          <Switch
            size="small"
            checked={element.shadowEnabled}
            onChange={(e) => { element.set({ shadowEnabled: e.target.checked }); commit(); }}
          />
        </Box>
        {element.shadowEnabled && (
          <>
            <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>{t('toolbar.blur', 'Blur')}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Slider
                size="small"
                value={element.shadowBlur}
                onChange={(_, v) => element.set({ shadowBlur: v })}
                min={0}
                max={50}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                type="number"
                value={element.shadowBlur}
                onChange={(e) => {
                  const v = parseInt(e.target.value);
                  if (!isNaN(v)) element.set({ shadowBlur: v });
                }}
                onFocus={(e) => e.target.select()}
                sx={{ width: 65, '& .MuiInputBase-input': { fontSize: 12, py: 0.5, textAlign: 'center' } }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>{t('toolbar.offsetX', 'Offset X')}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Slider
                size="small"
                value={element.shadowOffsetX}
                onChange={(_, v) => element.set({ shadowOffsetX: v })}
                min={-50}
                max={50}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                type="number"
                value={element.shadowOffsetX}
                onChange={(e) => {
                  const v = parseInt(e.target.value);
                  if (!isNaN(v)) element.set({ shadowOffsetX: v });
                }}
                onFocus={(e) => e.target.select()}
                sx={{ width: 65, '& .MuiInputBase-input': { fontSize: 12, py: 0.5, textAlign: 'center' } }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>{t('toolbar.offsetY', 'Offset Y')}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Slider
                size="small"
                value={element.shadowOffsetY}
                onChange={(_, v) => element.set({ shadowOffsetY: v })}
                min={-50}
                max={50}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                type="number"
                value={element.shadowOffsetY}
                onChange={(e) => {
                  const v = parseInt(e.target.value);
                  if (!isNaN(v)) element.set({ shadowOffsetY: v });
                }}
                onFocus={(e) => e.target.select()}
                sx={{ width: 65, '& .MuiInputBase-input': { fontSize: 12, py: 0.5, textAlign: 'center' } }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>{t('toolbar.color', 'Color')}</Typography>
            <input
              type="color"
              value={element.shadowColor || '#000000'}
              onChange={(e) => { element.set({ shadowColor: e.target.value }); commit(); }}
              style={{ width: 32, height: 32, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', padding: 0 }}
            />
          </>
        )}
      </Box>
    </Box>
  );
});

ImageEffectsPanel.displayName = 'ImageEffectsPanel';
export { ImageEffectsPanel };
